import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import LogoFallback from '/assets/img/bubbanlogo.jpg';

/**
 * HeroSection
 * - Responsive hero that matches background to the logo's main color.
 * - Uses TailwindCSS for layout and small inline styles for color variables.
 * - Props:
 *    - logoSrc: path to the logo image (string). If not provided the fallback import is used.
 *    - accent: hex color for background accent. If not provided, a sensible default is used.
 *
 * Usage:
 *  <HeroSection logoSrc="/assets/bubbanlogo.jpg" accent="#115e32" />
 */

export default function GlancePage({ logoSrc = LogoFallback, accent = '#115e32', pageTitle }) {
  // Inline CSS variables are used so Tailwind classes remain usable while the accent
  // color is dynamic and calculated/selected ahead of time.
	useEffect(() => {
		document.title = "At a Glance | Bandarban University"; // Sets the document title
	}, [pageTitle]); 
  
	const rootStyle = {
    '--accent': accent,
    // nicer subtle gradient using the accent color
    backgroundImage: `linear-gradient(135deg, var(--accent) 0%, rgba(17,94,50,0.85) 40%, rgba(255,255,255,0.03) 100%)`,
	};
	const data = {
		established: "7th February 2019",
		location:
		  "Bandarban University is located at Rowadaw Heights Main Rood, Bandarban town, Bandarban, Bangladesh.",
		faculties: [
		  "School of Arts, Social Science, and Law",
		  "School of Business Administration",
		  "School of Science & Engineering and Technology",
		],
		departments: [
		  "Department of English",
		  "Department of Governance and Development Studies",
		  "Department of Business Administration",
		  "Department of Computer Science and Engineering",
		],
		teachers: "50+",
		students: "500+",
		library:
		  "Books- 1,92,978 volume, (38,633 volume with banded publications)",
		research: "262 on-going Research Projects.",
		activities:
		  "Co-Curricular and socio-cultural programs are being conducted at Teacher-Student Centre & Shilpacharya Zainul Abedin Auditorium under the guidance of Students Affairs Division.",
	};

  return (
	<>
	
    <section
      className="relative w-full overflow-hidden px-6 sm:px-8 lg:px-16"
      style={rootStyle}
      aria-label="Hero"
    >
      {/* Decorative SVG shapes to give depth */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="w-full h-full"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          aria-hidden
        >
          <defs>
            <linearGradient id="g" x1="0" x2="1">
              <stop offset="0" stopOpacity="0.08" stopColor="#ffffff" />
              <stop offset="1" stopOpacity="0.02" stopColor="#000000" />
            </linearGradient>
          </defs>
          <rect width="1440" height="320" fill="url(#g)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto flex items-center justify-between py-20 lg:py-28">
        {/* Left column: logo (keeps relative positioning so it sits nicely on the hero) */}
        <div className="flex-shrink-0 lg:mr-8 hidden md:flex items-center">
          <img src={logoSrc} alt="Logo" className="w-28 h-28 object-contain drop-shadow-md" />
        </div>

        {/* Center: main content */}
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-sm">
            At a Glance of  <span className="inline-block" style={{ color: 'var(--accent)' }}>Bandarban University</span>
          </h1>

          <p className="mt-4 max-w-2xl mx-auto md:mx-0 text-white/90 text-lg sm:text-xl">
           
          </p>

        </div>
		
        
      </div>

      {/* Bottom wave to separate hero from page content */}
      <div className="-mt-6">
        <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-20">
          <path d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z" fill="white" opacity="0.92"></path>
        </svg>		
      </div>
		<div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
		
    </section>
	{/* Main Content */}
      <section className="py-12 container mx-auto px-6">
        {/* Established & Location */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <h2 className="text-xl font-bold text-green-700 mb-2">Established</h2>
            <p className="text-gray-700">{data.established}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <h2 className="text-xl font-bold text-green-700 mb-2">Location</h2>
            <p className="text-gray-700">{data.location}</p>
          </div>
        </div>

        {/* Faculties */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12 hover:shadow-xl transition">
          <h2 className="text-2xl font-bold text-green-700 mb-4">
            Faculties ({data.faculties.length})
          </h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            {data.faculties.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>

        {/* Departments */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12 hover:shadow-xl transition">
          <h2 className="text-2xl font-bold text-green-700 mb-4">
            Departments ({data.departments.length})
          </h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            {data.departments.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>

        {/* Teachers & Students */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <h2 className="text-xl font-bold text-green-700 mb-2">
              Number of Teachers
            </h2>
            <p className="text-gray-700">{data.teachers}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
            <h2 className="text-xl font-bold text-green-700 mb-2">
              Current Student’s Enrolment
            </h2>
            <p className="text-gray-700">{data.students}</p>
          </div>
        </div>

        {/* Library */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12 hover:shadow-xl transition">
          <h2 className="text-2xl font-bold text-green-700 mb-4">Central Library</h2>
          <p className="text-gray-700">{data.library}</p>
        </div>

        {/* Research */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12 hover:shadow-xl transition">
          <h2 className="text-2xl font-bold text-green-700 mb-4">Research</h2>
          <p className="text-gray-700">{data.research}</p>
        </div>

        {/* Extra Curricular */}
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition">
          <h2 className="text-2xl font-bold text-green-700 mb-4">
            Extra Curricular Activities
          </h2>
          <p className="text-gray-700">{data.activities}</p>
        </div>
      </section>
	</>
  );
}

GlancePage.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
};
